import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

@Injectable({
  providedIn: 'root'
})
export class ExcelService {
  constructor() { }

  async generateInventoryTemplate(
    categories: string[],
    uoms: string[],
    suppliers: string[],
    dataRows?: Array<{
      itemName?: string;
      itemCode?: string;
      category?: string;
      uom?: string;
      supplier?: string;
      purchasePrice?: number | string | null;
      purchaseQuantity?: number | string | null;
      lowStockThreshold?: number | string | null;
    }>
  ): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Aivor Raw Material Import');

    // Create validation sheet if it doesn't exist
    let validationSheet = workbook.getWorksheet('ValidationData');
    if (!validationSheet) {
      validationSheet = workbook.addWorksheet('ValidationData');
      validationSheet.state = 'hidden';
    }
    // Add instructions to a separate sheet so header stays at row 1 in the main sheet
    // const instructionsSheet = workbook.addWorksheet('Instructions');
    const instructions = [
      ['Aivor Raw Material Import - Instructions'],
      ['1. Fill in all fields'],
      ['2. Select values from dropdowns where available'],
      ['3. Do not modify the header row'],
      ['4. Delete any example rows before saving your data'],
      ['5. Save as .xlsx file before importing']
    ];
    instructions.forEach((row, index) => {
      const r = worksheet.addRow(row);
      if (index === 0) {
        r.font = { bold: true };
      }
    });
        // Add some example data
        // const exampleRow = worksheet.addRow({
        //   itemName: 'Example Item',
        //   itemCode: 'ITM-001',
        //   description: 'Example description',
        //   category: categories[0] || '',
        //   uom: uoms[0] || '',
        //   supplier: suppliers[0] || '',
        //   purchasePrice: 10.99,
        //   unitCost: 8.50,
        //   purchaseQuantity: 1,
        //   sellingPrice: 19.99,
        //   lowStockThreshold: 10,
        //   isActive: 'Yes'
        // });

        // // Style the example row
        // exampleRow.eachCell(cell => {
        //   cell.font = { color: { argb: 'FF808080' } };
        //   cell.fill = {
        //     type: 'pattern',
        //     pattern: 'solid',
        //     fgColor: { argb: 'FFF5F5F5' }
        //   };
        // });
    // Add headers with instructions
    worksheet.columns = [
      { header: 'Item Name*', key: 'itemName', width: 30 },
      { header: 'SKU', key: 'itemCode', width: 20 },
      { header: 'Category', key: 'category', width: 20 },
      { header: 'Unit of Measure', key: 'uom', width: 20 },
      { header: 'Supplier', key: 'supplier', width: 30 },
      { header: 'Purchase Price', key: 'purchasePrice', width: 15, style: { numFmt: '#,##0.00' } },
      { header: 'Purchase Quantity', key: 'purchaseQuantity', width: 20, style: { numFmt: '0' } },
      { header: 'Low Stock Threshold', key: 'lowStockThreshold', width: 20 }
    ];

    // Add data validation for dropdowns with configurable row range
    const addValidation = (
      col: string,
      type: 'list' | 'integer' | 'decimal',
      values?: string[],
      startRow: number = 2,
      endRow: number = 1000
    ) => {
      // For list validation, we need to create a named range
      if (type === 'list' && values) {
        const validationName = `List_${col}`;

        // Find the next empty column in the validation sheet
        let colIndex = 1;
        while (validationSheet.getCell(1, colIndex).value) {
          colIndex++;
        }

        // Add validation data to the next available column
        validationSheet.getCell(1, colIndex).value = validationName;
        values.forEach((value, index) => {
          validationSheet.getCell(index + 2, colIndex).value = value;
        });

        // Update the formula to reference the correct column
        const colLetter = String.fromCharCode(64 + colIndex); // Convert column number to letter

        // Add data validation using the named range
        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please select a value from the list',
            formulae: [`=ValidationData!$${colLetter}$2:$${colLetter}$${values.length + 1}`]
          };
        }
      } else if (type === 'integer') {
        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'whole',
            operator: 'greaterThan',
            formulae: [0],
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please enter a positive whole number'
          };
        }
      } else if (type === 'decimal') {
        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'decimal',
            operator: 'greaterThan',
            formulae: [0],
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please enter a positive number'
          };
        }
      }
    };

    // We'll apply validations after inserting data rows to ensure all populated lines are covered

    // (moved) We will protect the worksheet after inserting rows, unlocking cells, and applying validations

    // Lock all cells by default
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.protection = { locked: true };
      });
    });

    // If data rows are provided (e.g., failed raw materials), pre-populate them BEFORE unlocking
    // to avoid creating 1000 empty rows first (which would push data rows to the bottom)
    if (dataRows && dataRows.length) {
      dataRows.forEach(dr => {
        const toNumber = (val: any): number | null => {
          if (val === '' || val === null || val === undefined) return null;
          const n = typeof val === 'number' ? val : Number(val);
          return isNaN(n) ? null : n;
        };
        worksheet.addRow({
          itemName: (dr.itemName ?? '') as any,
          itemCode: (dr.itemCode ?? '') as any,
          category: (dr.category ?? '') as any,
          uom: (dr.uom ?? '') as any,
          supplier: (dr.supplier ?? '') as any,
          purchasePrice: toNumber(dr.purchasePrice) as any,
          purchaseQuantity: toNumber(dr.purchaseQuantity) as any,
          lowStockThreshold: toNumber(dr.lowStockThreshold) as any,
        });
      });
    }

    // Determine the last row and intended end range for validations/unlocking
    const lastRowNum = worksheet.lastRow?.number || 1;
    const endRow = Math.max(lastRowNum, 1000);

    // Unlock cells where users should enter data (rows 2..endRow)
    const dataEntryColumns = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
    dataEntryColumns.forEach(col => {
      for (let i = 2; i <= endRow; i++) {
        const cell = worksheet.getCell(`${col}${i}`);
        cell.protection = { locked: false };
      }
    });

    // Ensure validations exist for all data rows (including pre-populated)
    addValidation('C', 'list', categories, 2, endRow);  // Category
    addValidation('D', 'list', uoms, 2, endRow);        // UOM
    addValidation('E', 'list', suppliers, 2, endRow);   // Supplier
    addValidation('F', 'decimal', undefined, 2, endRow);           // Purchase Price
    addValidation('G', 'integer', undefined, 2, endRow);           // Purchase Quantity
    addValidation('H', 'integer', undefined, 2, endRow);           // Low Stock Threshold

    // Now protect the worksheet to prevent modification of data validation
    worksheet.protect('', {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      sort: false,
      autoFilter: false,
      pivotTables: false,
      scenarios: false,
      objects: false
    });

    // Lock the first row (headers)
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell(cell => {
      cell.protection = { locked: true };
      cell.font = { bold: true };
    });

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer({
      // Using type assertion to bypass the type check for ignoreNodes
      // This is needed as the type definition might be outdated
      ignoreNodes: ['worksheetProtection']
    } as any);
    const date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const formattedDate = `${day}_${month}_${year}`;
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const isFailedRM = Array.isArray(dataRows) && dataRows.length > 0;
    const rmFilename = isFailedRM
      ? `Aivor_RawMaterial_Failed_Rows_${formattedDate}.xlsx`
      : `Aivor_RawMaterial_Import_Template_${formattedDate}.xlsx`;
    saveAs(blob, rmFilename);
  }

  async generateProductsTemplate(
    categories: string[],
    uoms: string[],
    dataRows?: Array<{ itemName?: string; itemCode?: string; category?: string; uom?: string; productType?: string; sellingPrice?: number | string | null }>,
    itemTypes?: string[]
  ): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Aivor Product Import');

    // Create validation sheet if it doesn't exist
    let validationSheet = workbook.getWorksheet('ValidationData');
    if (!validationSheet) {
      validationSheet = workbook.addWorksheet('ValidationData');
      validationSheet.state = 'hidden';
    }

    // Add instructions on a separate sheet to keep header at row 1 in main sheet
    // const instructionsSheet = workbook.addWorksheet('Instructions');
    const instructions = [
      ['Aivor Product Import - Instructions'],
      ['1. Fill in the fields below'],
      ['2. Select values from dropdowns where available'],
      ['3. Do not modify the header row'],
      ['4. Delete any example rows before saving your data'],
      ['5. Save as .xlsx file before importing']
    ];

    instructions.forEach((row, index) => {
      const instructionRow = worksheet.addRow(row);
      if (index === 0) {
        instructionRow.font = { bold: true };
      }
    });

    // Add headers
    worksheet.columns = [
      { header: 'Item Name*', key: 'itemName', width: 30 },
      { header: 'SKU', key: 'itemCode', width: 20 },
      { header: 'Category', key: 'category', width: 20 },
      { header: 'Unit of Measure', key: 'uom', width: 20 },
      { header: 'Product Type', key: 'productType', width: 20 },
      { header: 'Selling Price', key: 'sellingPrice', width: 18, style: { numFmt: '#,##0.00' } }
    ];

    // Add example row
    // const exampleRow = worksheet.addRow({
    //   itemName: 'Example Product',
    //   itemCode: 'SKU-001',
    //   category: categories[0] || '',
    //   uom: uoms[0] || '',
    //   sellingPrice: 100.0
    // });
    // exampleRow.eachCell(cell => {
    //   cell.font = { color: { argb: 'FF808080' } };
    //   cell.fill = {
    //     type: 'pattern',
    //     pattern: 'solid',
    //     fgColor: { argb: 'FFF5F5F5' }
    //   };
    // });

    // Local helper to add validations with configurable row range
    const addValidation = (
      col: string,
      type: 'list' | 'integer' | 'decimal',
      values?: string[],
      startRow: number = 2,
      endRow: number = 1000
    ) => {
      if (type === 'list' && values) {
        // Find the next empty column in the validation sheet
        let colIndex = 1;
        while (validationSheet.getCell(1, colIndex).value) {
          colIndex++;
        }

        // Label the list in row 1 so subsequent lists advance to a new column
        validationSheet.getCell(1, colIndex).value = `List_${col}`;

        // Add validation data to the next available column (rows start at 2)
        values.forEach((value, index) => {
          validationSheet.getCell(index + 2, colIndex).value = value;
        });

        // Convert column number to letter (ValidationData)
        const colLetter = String.fromCharCode(64 + colIndex);

        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'list',
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please select a value from the list',
            formulae: [`=ValidationData!$${colLetter}$2:$${colLetter}$${values.length + 1}`]
          } as any;
        }
      } else if (type === 'integer') {
        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'whole',
            operator: 'greaterThan',
            formulae: [0],
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please enter a positive whole number'
          } as any;
        }
      } else if (type === 'decimal') {
        for (let i = startRow; i <= endRow; i++) {
          const cell = worksheet.getCell(`${col}${i}`);
          cell.dataValidation = {
            type: 'decimal',
            operator: 'greaterThanOrEqual',
            formulae: [0],
            allowBlank: false,
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please enter a valid amount'
          } as any;
        }
      }
    };

    // Defer validations until after rows are inserted so we don't create 1000 empty rows first

    // (moved) We'll protect after inserting rows, unlocking, and applying validations

    // Lock all cells by default
    worksheet.eachRow({ includeEmpty: true }, (row) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.protection = { locked: true };
      });
    });

    // If data rows are provided (e.g., failed finished goods), pre-populate them BEFORE unlocking
    // to avoid creating 1000 empty rows first (which would push data rows to the bottom)
    if (dataRows && dataRows.length) {
      dataRows.forEach(dr => {
        worksheet.addRow({
          itemName: (dr.itemName ?? '') as any,
          itemCode: (dr.itemCode ?? '') as any,
          category: (dr.category ?? '') as any,
          uom: (dr.uom ?? '') as any,
          productType: (dr.productType ?? '') as any,
          // Allow number or empty for selling price
          sellingPrice: (dr.sellingPrice === '' || dr.sellingPrice === null || dr.sellingPrice === undefined)
            ? null
            : (typeof dr.sellingPrice === 'number' ? dr.sellingPrice : Number(dr.sellingPrice)) as any
        });
      });
    }

    // Determine last populated row and compute end range for unlocking/validations
    const lastRowNumFG = worksheet.lastRow?.number || 1;
    // If we have pre-populated data rows, only validate/unlock up to the last used row
    // Otherwise, provide room up to 1000 for manual entry
    const endRowFG = (dataRows && dataRows.length) ? lastRowNumFG : 1000;

    // Unlock data entry cells (A..F, rows 2..endRowFG)
    const dataEntryColumnsFG = ['A', 'B', 'C', 'D', 'E', 'F'];
    dataEntryColumnsFG.forEach(col => {
      for (let i = 2; i <= endRowFG; i++) {
        const cell = worksheet.getCell(`${col}${i}`);
        cell.protection = { locked: false };
      }
    });

    // Ensure validations apply to all populated rows (including pre-populated)
    addValidation('C', 'list', categories, 2, endRowFG); // Category
    addValidation('D', 'list', uoms, 2, endRowFG);       // UOM
    if (itemTypes && itemTypes.length) {
      addValidation('E', 'list', itemTypes, 2, endRowFG); // Product Type
    }
    // Selling Price is optional — allow the cell to be left blank
    for (let i = 2; i <= endRowFG; i++) {
      const cell = worksheet.getCell(`F${i}`);
      cell.dataValidation = {
        type: 'decimal',
        operator: 'greaterThanOrEqual',
        formulae: [0],
        allowBlank: true,
        showErrorMessage: true,
        errorTitle: 'Invalid Input',
        error: 'Please enter a valid amount'
      } as any;
    }

    // Now protect after unlocking and validations
    worksheet.protect('', {
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      sort: false,
      autoFilter: false,
      pivotTables: false,
      scenarios: false,
      objects: false
    });

    // Lock headers (row 1)
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell(cell => {
      cell.protection = { locked: true };
      cell.font = { bold: true };
    });

    // Export
    const buffer = await workbook.xlsx.writeBuffer({
      ignoreNodes: ['worksheetProtection']
    } as any);
    const date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const formattedDate = `${day}_${month}_${year}`;
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const isFailedFG = Array.isArray(dataRows) && dataRows.length > 0;
    const fgFilename = isFailedFG
      ? `Aivor_Product_Failed_Rows_${formattedDate}.xlsx`
      : `Aivor_Product_Import_Template_${formattedDate}.xlsx`;
    saveAs(blob, fgFilename);
  }

  async readInventoryFile(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = new ExcelJS.Workbook();

        workbook.xlsx.load(data)
          .then(workbook => {
            const worksheet = workbook.worksheets[0];
            const items: any[] = [];
            const headers: any = {};

            // Get headers
            worksheet.getRow(1).eachCell((cell, colNumber) => {
              if (cell.value) {
                headers[cell.value.toString().toLowerCase().replace(/\s+/g, '')] = colNumber;
              }
            });

            // Process rows
            for (let i = 2; i <= worksheet.rowCount; i++) {
              const row = worksheet.getRow(i);
              if (!row.getCell(1).value) continue; // Skip empty rows

              const item: any = {};
              Object.keys(headers).forEach(header => {
                const cell = row.getCell(headers[header]);
                item[header] = cell.value;
              });

              // Convert Yes/No to boolean
              if ('isactive' in item) {
                item.isactive = item.isactive?.toString().toLowerCase() === 'yes';
              }

              // Carry the real spreadsheet row number so import errors can
              // point the user at the offending cell. Array position is not
              // the row number once blank rows are skipped.
              Object.defineProperty(item, '__excelRow', {
                value: i,
                enumerable: false,
                writable: false
              });

              items.push(item);
            }

            resolve(items);
          })
          .catch(error => reject(error));
      };

      reader.onerror = error => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }
}
